import { GenericResponse } from '@/common/common_utils';
import { ENDPOINTS } from '@/constants/endpoints';
import { ConciergeLiveMap } from '@/modules/concierge/map/ConciergeLiveMap';
import { fetchActivityRouteData } from '@/modules/concierge/map/useInitialRoute';
import type { GoogleMapsRoutingResponse } from '@/modules/map/engine/GoogleTypes';
import type { GetOrderDetailsResponse } from '@/modules/vehicle/engine/ActivityTypes';
import type { X4bActivityDto } from '@/modules/vehicle/engine/X4bActivityDto';
import { axiosInstance } from '@/services/axios.service';
import moment from 'moment';
import { Timeline } from 'primereact/timeline';
import { classNames } from 'primereact/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { MdOutlineRadioButtonChecked } from 'react-icons/md';

type Props = {
	activity?: X4bActivityDto | null;
};

interface TimelineEvent {
	time: string | null;
	name: string | null;
	color: string;
}

const formatTime = (dateTime: string) => {
	const d = new Date(dateTime);
	return d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const formatDuration = (start: string, end: string) => {
	const duration = moment.duration(moment(end).diff(moment(start)));
	const h = Math.floor(duration.asHours());
	const m = duration.minutes();
	const s = duration.seconds();
	if (h > 0) return `${h}h ${m}m ${s}s`;
	if (m > 0) return `${m}m ${s}s`;
	return `${s}s`;
};

const getActionColor = (action: string) => {
	switch (action) {
		case 'Completed':
			return 'text-accent60';
		case 'Arrived To Pickup':
			return 'text-primary';
		case 'Accept':
			return 'text-success70';
		default:
			return 'text-neutral4';
	}
};

const getTimelineMarkerColor = (action: string) => {
	switch (action) {
		case 'Accept':
			return 'text-green-500';
		case 'Arrived To Pickup':
			return 'text-blue-900';
		case 'Completed':
			return 'text-red-500';
		default:
			return 'text-blue-500';
	}
};

const OrderHistoryTimeline = (props: Props) => {
	const { activity } = props;
	const [orderDetails, setOrderDetails] = useState<GetOrderDetailsResponse | null>(null);
	const [routingData, setRoutingData] = useState<GoogleMapsRoutingResponse | null>(null);

	const tripIdSetterRef = useRef<(tripId: string) => void>(undefined);
	const bindTripIdSetter = useCallback((setter: (tripId: string) => void) => {
		tripIdSetterRef.current = setter;
	}, []);

	const orderUuidSetterRef = useRef<(orderUuid: string) => void>(undefined);
	const bindOrderUuidSetter = useCallback((setter: (orderUuid: string) => void) => {
		orderUuidSetterRef.current = setter;
	}, []);

	useEffect(() => {
		if (!activity?.XpressId) return;

		fetchActivityRouteData(
			activity,
			activity.Id,
			activity.XpressId,
			setRoutingData,
			orderUuidSetterRef,
			tripIdSetterRef,
			true
		);

		axiosInstance
			.post<GenericResponse<GetOrderDetailsResponse>>(
				ENDPOINTS['/api/X4bActivity/GetOrderDetails'],
				{ BusinessOrderId: activity.XpressId },
			)
			.then((res) => setOrderDetails(res.data.Data ?? null))
			.catch(() => setOrderDetails(null));
	}, [activity?.XpressId, activity?.Id, activity?.Status]);

	const attempts = orderDetails?.Attempts ?? [];

	const sessionTime = attempts.length >= 2
		? formatDuration(attempts[0].DateTime, attempts[attempts.length - 1].DateTime)
		: '-';

	const timelineEvents: TimelineEvent[] = attempts.map((a) => ({
		time: formatTime(a.DateTime),
		name: a.ActionText,
		color: getTimelineMarkerColor(a.ActionText),
	}));

	const customizedMarker = (item: TimelineEvent) => {
		return <MdOutlineRadioButtonChecked size={20} className={item.color} />;
	};

	const customizedContent = (item: TimelineEvent) => {
		return (
			<div className="flex justify-between">
				<p className="text-body-medium text-neutral17">{item.name}</p>
				<p className="text-body-medium text-neutral50">{item.time}</p>
			</div>
		);
	};

	return (
		<div className="flex flex-col space-y-7">
			<h3 className="text-label-large font-semibold text-neutral17 py-3">
				Order Timeline
			</h3>
			<div className="grid grid-cols-1 md:grid-cols-2 space-y-5 md:space-y-0 md:gap-6">
				<div className="" style={{ marginTop: "-8px" }}>
					<div className="grid grid-cols-2">
						<p className="py-2 px-3 text-title-medium text-neutral50">
							Priority
						</p>
						<p className="py-2 px-3 text-right text-title-medium text-neutral4">
							Normal
						</p>
					</div>
					<div className="grid grid-cols-2">
						<p className="py-2 px-3 text-title-medium text-neutral50">
							Session Time
						</p>
						<p className="py-2 px-3 text-right text-title-medium text-neutral4">
							{sessionTime}
						</p>
					</div>
					<div className="grid grid-cols-2">
						<p className="py-2 px-3 text-title-medium text-neutral50">
							Attempts
						</p>
						<p className="py-2 px-3 text-right text-title-medium text-neutral4">
							{orderDetails?.AttemptsCount ?? '-'}
						</p>
					</div>
					<div>
						{attempts.map((a, i) => (
							<div
								key={`${a.DateTime}-${i}`}
								className="grid grid-cols-3 pl-6"
							>
								<p className="py-2 px-3 text-title-medium text-neutral50">
									{formatTime(a.DateTime)}
								</p>
								<p className="py-2 px-3 text-right text-title-medium text-neutral4">
									Single
								</p>
								<p
									className={classNames(
										'py-2 px-3 text-right',
										getActionColor(a.ActionText),
									)}
								>
									{a.ActionText}
								</p>
							</div>
						))}
					</div>
				</div>

				<div className="">
					<Timeline
						value={timelineEvents}
						marker={customizedMarker}
						content={customizedContent}
						className="w-full h-full"
						align="left"
						pt={{
							content: {
								className: 'font-poppins',
							},
							opposite: {
								className: 'hidden',
							},
						}}
					/>
				</div>
			</div>
			{activity ? (
				<ConciergeLiveMap
					key={activity.XpressId}
					bindTripIdSetter={bindTripIdSetter}
					bindOrderUuidSetter={bindOrderUuidSetter}
					pickupLocationName={activity.Pickup?.PlaceName || activity.Pickup?.Address || undefined}
					dropOffLocationName={activity.Dropoff?.PlaceName || activity.Dropoff?.Address || undefined}
					mapDivClassName="w-full h-[296px] rounded-lg"
					activity={activity}
					initialRoute={routingData}
					showCompletedRoute={true}
				/>
			) : (
				<img
					src="/images/svg/map-overview.svg"
					alt="Delivery route map"
					width={788}
					height={296}
					className="w-full"
				/>
			)}
		</div>
	);
};

export default OrderHistoryTimeline;
